import { memo, useEffect, useState } from "react";
import { checkChatSocket, recoverChatSocket } from "../../../../redux/services/chatApi";
import { checkSipSocket, recoverSipSocket } from "../../../../redux/middleware/sipMiddleware";
import { useTypedDispatch, useTypedSelector } from "../../../../redux/hooks";
import { logout, selectCurrentUser, selectIsNebulaChat } from "../../../../redux/slices";
import { StyledButton } from "../../../../components/StyledComponents";
import styles from './styles.module.scss';
import LoadingSpinner from "../../../../components/loading/LoadingSpinner";
import { checkPresenceSocket, recoverPresenceSocket } from "../../../../redux/services/presneceApi";
import { checkConversationSocket, recoverConversationSocket } from "../../../../redux/services/nebulaChat";
import { EVENT_SOCKET_STATUS } from "../../../../redux/store";

export type RetryContext = 'socket' | 'token_login';

interface StatusFailureProps {
    context: RetryContext;
    retryAction: () => void;
}

export const resetAuthRetries = () => {
    localStorage.setItem(`retry_attempts_token_login`, '0');
    localStorage.setItem(`retry_attempts_socket`, '0');
}

function parseKey(context: RetryContext): string {
    return `retry_attempts_${context}`;
}

export function setRetryKey(context: RetryContext, count: number | undefined) {
    const key = parseKey(context);

    if (Number.isNaN(count as number)) {
        localStorage.removeItem(key)
    } else {
        localStorage.setItem(key, String(count));
    }
}

export const StatusFailure: React.FC<StatusFailureProps> = ({ context, retryAction }) => {
    const dispatch = useTypedDispatch();

    const key = parseKey(context);
    let local_retries = parseInt(localStorage.getItem(key) || '0');
    if (typeof local_retries !== 'number') {
        local_retries = 0;
    }

    const [tries, setTries] = useState(local_retries);

    const btn = (
        <StyledButton buttonStyle='primary' onClick={() => {
            retryAction();
            setRetryKey('socket', tries + 1);
            setTries(tries + 1);
        }}>
            <p>{tries === 0 ? 'Reconnect Now' : 'Try Again'}</p>
        </StyledButton>
    );

    const title = tries === 0 ? "Connection Lost" : "Unable to Reconnect";

    const title_elem = <h3>{title}</h3>;

    if (tries === 0) {
        return (<>
            {title_elem}
            <p>Your session has either expired or been disconnected. You may be temporarily offline or unable to connect to our service</p>
            {btn}
        </>)
    }

    if (tries < 3) {
        return (<>
            {title_elem}
            <p><span>Your session couldn&apos;t be reconnected.</span><span>Please try again!</span></p>
            {btn}
        </>)
    }

    return (<>
        {title_elem}
        <p>This session could not be reconnected. Please login again to activate a new session.</p>
        <StyledButton buttonStyle='primary' onClick={() => {
            dispatch(logout())
        }}>
            <p>Go to Login</p>
        </StyledButton>
    </>)
};

const GRACE_MS = 1_000;
const POLL_MS = 15_000;
const MIN_RECOVER_MS = 10_000;

const StatusSocketController = memo(() => {
    const dispatch = useTypedDispatch();
    const chatEnabled = useTypedSelector(state => selectCurrentUser(state)?.chat_enabled);
    const isNebulaChat = useTypedSelector(selectIsNebulaChat);

    const [show, setShow] = useState(false);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let pollInterval: ReturnType<typeof setInterval> | null = null;
        let lastRecoverAt = 0;

        const isHealthy = () => {
            const sip = checkSipSocket();
            const presence = checkPresenceSocket();
            const chat = isNebulaChat ? checkConversationSocket() : checkChatSocket();

            if (sip !== 'ok' || presence !== 'ok' || chat !== 'ok') {
                console.log("Check health", { sip, presence, chat });
            }

            if (
                (sip === 'no_connection' || sip === 'no_auth')
                || presence !== 'ok'
            ) {
                return false;
            }

            if (!chatEnabled) {
                return true;
            }

            return chat === 'ok';
        };

        const recoverAll = () => {
            lastRecoverAt = Date.now();
            if (checkSipSocket() !== 'ok') recoverSipSocket();
            if (checkPresenceSocket() !== 'ok') recoverPresenceSocket();
            if (chatEnabled) {
                const chat = isNebulaChat ? checkConversationSocket() : checkChatSocket();
                if (chat !== 'ok') {
                    if (isNebulaChat) {
                        dispatch(recoverConversationSocket());
                    } else {
                        recoverChatSocket();
                    }
                }
            }
        };

        const tryRecover = () => {
            if (Date.now() - lastRecoverAt < MIN_RECOVER_MS) return;
            recoverAll();
        };

        const evaluate = () => {
            if (isHealthy()) {
                if (timer) { clearTimeout(timer); timer = null; }
                setShow(false);
            } else if (!timer) {
                timer = setTimeout(() => {
                    timer = null;
                    setShow(true);
                    tryRecover();
                }, GRACE_MS);
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible' && !isHealthy()) {
                tryRecover();
            }
        };

        pollInterval = setInterval(() => {
            if (!isHealthy()) tryRecover();
        }, POLL_MS);

        evaluate();
        window.addEventListener(EVENT_SOCKET_STATUS, evaluate);
        window.addEventListener('online', tryRecover);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            if (timer) clearTimeout(timer);
            if (pollInterval) clearInterval(pollInterval);
            window.removeEventListener(EVENT_SOCKET_STATUS, evaluate);
            window.removeEventListener('online', tryRecover);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [chatEnabled, isNebulaChat]);

    return (
        <div className={styles.reconnect_banner} data-hidden={show ? null : ' '} aria-hidden={!show}>
            <p>
                Connection unstable, attempting to re establish
            </p>
            <LoadingSpinner color="mamba" />
        </div>
    )
});

export default StatusSocketController;
